import { useAuth } from "@/hooks/use-auth";
import { useLocation, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useEffect, useState } from "react";
import { Camera, Clock, CreditCard, ShieldCheck, Loader2 } from "lucide-react";
import NeedHelp from "@/components/NeedHelp";
import logo from "./InMyNameKYCTransparent.png";

export default function WelcomeConfirmationPage() {
  const { user, isLoading } = useAuth();
  const [_, setLocation] = useLocation();
  const [pageReady, setPageReady] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [loading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      if (!user) {
        console.log("No user found on welcome page, redirecting to auth...");
        setLocation("/landing-page");
        return;
      }

      if (user && !user.isFirstLogin) {
        console.log("User not on first login, redirecting to onbaording...");
        setLocation("/onboarding");
        return;
      }

      setPageReady(true);
    }
  }, [user, isLoading, setLocation]);

  const handleStartVerification = async () => {
    setIsLoading(true);
    setIsAgreed(true);

    // Simulate network delay (2 seconds)
    await new Promise((resolve) => setTimeout(resolve, 2000));

    const response = await fetch("/api/user-update-consent", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        userId: user.id,
      }),
    });

    const responseDocs = await fetch("/api/documents", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    window.scrollTo(0, 0);
    sessionStorage.setItem("welcome_viewed", "true");

    setIsAgreed(false);
    setLocation("/onboarding");
  };

  // Show loading state while checking authentication or redirecting
  if (isLoading || !pageReady) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        style={{ backgroundColor: "#f5f5f5" }}
      >
        <div className="text-center">
          <Loader2
            className="h-8 w-8 animate-spin mx-auto mb-4"
            style={{ color: "#316363" }}
          />
          <p className="text-sm" style={{ color: "#316363" }}>
            Preparing your verification...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ backgroundColor: "#f5f5f5" }}
    >
      {/* Header area with teal gradient */}
      <div
        className="p-[32px]"
        style={{
          background: "linear-gradient(45deg, #2D5758 0%, #346D6D 100%)",
          borderBottomLeftRadius: "16px",
          borderBottomRightRadius: "16px",
        }}
      >
        <div className="max-w-md mx-auto">
          <div className="flex items-center mb-3">
            <img className="w-[95px]  h-[31px]" src={logo} />
          </div>
          <p className="text-[24px] font-normal text-[#ffffff]">
            👋 Welcome to
          </p>
          <p className="text-[24px] font-semibold text-[#ffffff]">
            InMyNameKYC
          </p>
          <p className="text-[14px] font-normal text-[#DAEDEB] mt-4">
            We're here to help you verify your identity securely and quickly. To
            continue, we need your consent to process your information:
          </p>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 p-4" style={{ backgroundColor: "#ffffff" }}>
        <div className="max-w-md mx-auto">
          <Card className="shadow-none border-0 mb-4 rounded-2xl">
            <CardContent className="shadow-none py-2 px-2 border-0">
              <ul className="space-y-3 mb-4">
                <li className="flex items-start py-2">
                  <div
                    className="p-2 rounded-xl mr-3  h-[35px] w-[34px]"
                    style={{ backgroundColor: "#ccf9ee" }}
                  >
                    <p>
                      <CreditCard
                        className="h-[18px] w-[20px]"
                        style={{ color: "#28a89a" }}
                      />
                    </p>
                  </div>
                  <div>
                    <span
                      className="text-sm font-semibold"
                      style={{ color: "#438988" }}
                    >
                      Valid Document
                    </span>
                    <p className="text-[12px] " style={{ color: "#A4A7AE" }}>
                      Passport or Driving Licence
                    </p>
                  </div>
                </li>

                <li className="flex items-start py-2">
                  <div
                    className="p-2 rounded-xl mr-3  h-[35px] w-[34px]"
                    style={{ backgroundColor: "#ccf9ee" }}
                  >
                    <Camera
                      className="h-[18px] w-[20px]"
                      style={{ color: "#28a89a" }}
                    />
                  </div>
                  <div>
                    <span
                      className="text-sm font-semibold"
                      style={{ color: "#438988" }}
                    >
                      Camera Access
                    </span>
                    <p className="text-[12px] " style={{ color: "#A4A7AE" }}>
                      On your phone or computer to capture document images
                    </p>
                  </div>
                </li>

                <li className="flex items-start py-2">
                  <div
                    className="p-2 rounded-xl mr-3 h-[35px] w-[34px]"
                    style={{ backgroundColor: "#ccf9ee" }}
                  >
                    <Clock
                      className="h-[18px] w-[20px]"
                      style={{ color: "#28a89a" }}
                    />
                  </div>
                  <div>
                    <span
                      className="text-sm font-semibold"
                      style={{ color: "#438988" }}
                    >
                      A Few Minutes
                    </span>
                    <p className="text-[12px] " style={{ color: "#A4A7AE" }}>
                      In a well-lit area to complete the verification process
                    </p>
                  </div>
                </li>
              </ul>

              <div
                className="border rounded-2xl p-4 mt-4"
                style={{
                  backgroundColor: "#F4F9F8",
                  borderColor: "#B4DBD8",
                }}
              >
                <div className="flex">
                  <div className="mt-0.5" style={{ color: "#77dacf" }}>
                    <ShieldCheck className="h-[19px] w-[16px]" />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs" style={{ color: "#438988" }}>
                      Your details will be checked using Entrust, our trusted
                      identity partner.
                    </p>
                  </div>
                </div>
                <div className="flex my-3">
                  <div className="mt-0.5" style={{ color: "#77dacf" }}>
                    <ShieldCheck className="h-[19px] w-[16px]" />
                  </div>
                  <div className="ml-3 ">
                    <p className="text-xs" style={{ color: "#438988" }}>
                      We use your information only for legal identity and
                      compliance checks – never for marketing.
                    </p>
                  </div>
                </div>
                <div className="flex">
                  <div className="mt-0.5" style={{ color: "#77dacf" }}>
                    <ShieldCheck className="h-[19px] w-[16px]" />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs" style={{ color: "#438988" }}>
                      You can learn more about how we use your data in our{" "}
                      <Link href="/privacy">
                        {" "}
                        <b style={{ textDecoration: "underline" }}>
                          Privacy Policy.
                        </b>
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Consent checkbox */}
          <div
            className="mb-4 px-4 rounded-2xl"
            style={{ backgroundColor: "#ffffff" }}
          >
            <div className="flex items-start">
              <input
                type="checkbox"
                id="consent-checkbox"
                checked={isAgreed}
                onChange={(e) => setIsAgreed(e.target.checked)}
                className="mr-3 h-4 w-4 rounded border-2"
                style={{
                  accentColor: "#346D6D",
                  borderColor: "#B4DBD8",
                }}
              />
              <label
                htmlFor="consent-checkbox"
                className="text-xs cursor-pointer"
                style={{ color: "#438988" }}
              >
                I agree and consent to proceed with identity verification
              </label>
            </div>
          </div>

          <Button
            onClick={handleStartVerification}
            disabled={!isAgreed || loading}
            className="w-full h-12 text-sm font-medium rounded-2xl shadow-lg text-white border-0"
            style={{
              background:
                !isAgreed || loading
                  ? "#A4A7AE"
                  : "linear-gradient(45deg, #2D5758 0%, #346D6D 100%)",
              opacity: !isAgreed || loading ? 0.6 : 1,
              cursor: !isAgreed || loading ? "not-allowed" : "pointer",
            }}
          >
            {loading
              ? "Starting verification..."
              : "Agree & Start Verification"}
          </Button>
        </div>
      </div>
      <NeedHelp />
    </div>
  );
}
